import { createContext, useCallback, useContext, useEffect } from 'react';
import { useNavigate, useOutlet } from 'react-router-dom';

import { Icon } from '../ui/Icon.js';

interface DetailOverlayValue {
  /** True when the detail page is rendered inside a list page's overlay panel. */
  inOverlay: boolean;
  close: () => void;
}

const DetailOverlayContext = createContext<DetailOverlayValue>({
  inOverlay: false,
  close: () => undefined,
});

export function useDetailOverlay(): DetailOverlayValue {
  return useContext(DetailOverlayContext);
}

interface DetailOverlayProps {
  /** List route to return to when the panel is dismissed. */
  listPath: string;
}

/**
 * Renders the matched child route (an entity detail page) in a near-full-width panel
 * floating above the list page, so list filters and paging survive opening a record.
 * Sits below `Modal`'s z-50 so dialogs opened inside the detail page stack on top.
 */
export function DetailOverlay({ listPath }: DetailOverlayProps): JSX.Element | null {
  const outlet = useOutlet();
  const navigate = useNavigate();
  const open = Boolean(outlet);

  const close = useCallback(() => {
    navigate(listPath);
  }, [navigate, listPath]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    // Offset by the sidebar width so the panel is sized/centered against the working area only.
    <div className="fixed inset-y-0 left-0 right-0 z-40 flex items-start justify-center p-2 sm:p-4 lg:left-64">
      <button
        type="button"
        aria-label="Close panel"
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative z-10 flex h-[calc(100vh-1rem)] w-[95%] flex-col overflow-hidden rounded-xl bg-slate-50 shadow-2xl sm:h-[calc(100vh-2rem)]"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-lg bg-white/80 p-1 text-slate-400 shadow-sm hover:bg-slate-100 hover:text-slate-600"
        >
          <Icon name="close" className="h-5 w-5" />
        </button>
        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          <DetailOverlayContext.Provider value={{ inOverlay: true, close }}>
            {outlet}
          </DetailOverlayContext.Provider>
        </div>
      </div>
    </div>
  );
}
